import React from 'react';
import DocumentPage, {
  Section,
  Paragraph,
  DoDoNot,
  HazardTable,
} from './DocumentPage';

const BenchGrinderDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Bench Grinder – RA & SSOW"
    documentHeading="Risk Assessment 06 & Safe System of Work: Bench Grinders"
    meta={[
      ['Document Reference', 'PVS-RAMS-BG-001'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Area / Location', 'Workshop'],
      ['Activity', 'Use and operation of a bench grinder'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Bench Grinder – RA & SSOW',
      documentReference: 'PVS-RAMS-BG-001',
      processActivity: 'Use of Bench Grinder',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Introduction">
      <Paragraph>
        There are a number of hazards present when operating a bench grinder, these include being
        caught in the machinery, grinding wheels exploding, flying debris or particles entering the
        eye or getting fingers caught or trapped. The following are common faults found with bench
        grinders in workshops: guards damaged or missing; cracks or defects in the wheel; appropriate
        PPE not worn during operation; guards and work rests not adjusted properly.
      </Paragraph>
    </Section>

    <Section number="2" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard Category', 'Hazards', 'Control Measures Already in Place', 'Additional Control Measures Required']}
        rows={[
          [
            'Fire and Electrical Hazards',
            [
              'Electrocution / burns / fire from faulty equipment.',
              'Potential fire risk from sparks created when grinding metal etc.',
            ],
            [
              'Staff have been provided with sufficient information, instruction and training.',
              'The bench grinder is inspected at periodic intervals.',
              'Flammable materials are kept away from the bench grinder.',
            ],
            'None required.',
          ],
          [
            'Physical Hazards',
            [
              'Ejection from wheel bursting or particles / materials being ejected, resulting in damage to the eyes, face etc.',
              'Contact with the wheel / hot work pieces resulting in cuts, burns, abrasion etc.',
              "Entrapment – operator's fingers or loose clothing being caught in moving parts of the machinery.",
            ],
            [
              'Staff have been provided with sufficient information, instruction and training.',
              'The bench grinder is bolted / secured in place.',
            ],
            [
              'Guards / eye shields should be in place and be maintained.',
              'Wheels should only be changed by persons with appropriate abrasive wheel training.',
            ],
          ],
          [
            'Hazardous Substances',
            ['Inhalation of particulate dust created by grinding etc.'],
            [
              'Staff have been provided with sufficient information, instruction and training.',
              'Use of the bench grinder is such that hazardous levels of dust are not expected to be generated.',
            ],
            'None required.',
          ],
        ]}
      />
    </Section>

    <Section number="3" title="Safe System of Work">
      <DoDoNot
        doItems={[
          'Carry out a visual check of the equipment before use – check the guards are in place, the grinding wheel is in good condition, etc. Report any defects.',
          'Wear eye protection (standard spectacles are not adequate).',
          'Keep your hands and body well clear of the wheels when operating the grinder.',
          'Take precautions against entanglement with moving parts by wearing tight fitting clothing, tying back long hair, removing loose jewellery, etc.',
          'Check that all persons are clear of the work area.',
          'Check the work bench and the area around the equipment is free of any obstructions.',
          'Ensure the tool rest is properly secured and adjusted.',
          'Check the space between the wheel face and the tool rest is kept to a minimum.',
          'Check the face of the wheel is evenly dressed.',
          'Keep the area clear of trailing electrical cables.',
          'Unplug the tool from the power supply before making any adjustments.',
          'When changing the wheel (only if trained and authorised): unplug and switch off the machine; check that grinding wheels are stationary; use the correct tools to remove the old wheel; check the new wheel for damage; check the spindle speed of the machine and the wheel speed rating are compatible; use the correct tools to secure the wheel; readjust tool rests so they remain as close to the wheel as possible (maximum 2mm from stone/wheel face) and tighten securely; give the wheel a test run before grinding.',
        ]}
        doNotItems={[
          'Operate any defective equipment.',
          'Operate the bench grinder unless the guards are in place and you are wearing eye protection.',
          'Exceed the maximum operating speed which is displayed on the equipment.',
          'Misuse the equipment.',
          'Interfere with any safety device or guards fitted.',
          'Touch or attempt to touch a revolving wheel.',
          'Leave the equipment running when unattended.',
          'Use the side of the wheel for grinding.',
          'Use a worn or damaged stone / wheel.',
          'Grind materials containing asbestos.',
          'Use wheels that are not designed for the machine you are using.',
          'Touch the work piece after grinding until it has had time to cool.',
          'Over tighten wheels.',
          'Force the grinder to achieve a task it was not designed to perform.',
          'Switch the grinder on whilst the wheel is in contact with the work piece.',
          'Use the grinder when flammable liquids, solids or gasses are present.',
        ]}
      />
    </Section>

    <Section number="4" title="Reminder">
      <Paragraph className="font-semibold">
        You have a duty not only to protect your own health and safety but also to ensure that your
        actions do not put others around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default BenchGrinderDoc;
